import React, { Fragment, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import {
  colorBlack,
  colorPrimary,
  desktopMin,
  tabletMax,
  toCamelCase,
  toCssColor,
} from '~/common/utils';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { useRTETooltipPositionCorrector } from '~/common/hooks/use-rteTooltipPositionCorrector';
import {
  Button,
  ButtonProps,
  ButtonTypeEnum,
  CustomCarousel,
  SvgIcon,
} from '~/common/components/ui-elements';
import { CallToAction, Link } from '~/common/models';

interface IOwnerReview {
  author?: string;
  message?: string;
  date?: Date;
  reviewUrl?: Link;
}

interface IOwnerReviewsProps {
  id: number;
  title?: string;
  titleDisclaimer?: string;
  reviewList?: IOwnerReview[];
  reviewCTA?: CallToAction;
  backgroundColor?: string;
  fontColor?: string;
  tileBackgroundColor?: string;
  tileFontColor?: string;
}

const breakpoints = {
  desktop: {
    breakpoint: { max: 4000, min: desktopMin },
    items: 3,
  },
  mobile: {
    breakpoint: { max: tabletMax, min: 0 },
    items: 1,
  },
};

const ExpandableRTERenderer: React.FC<{
  html: string;
  lines: number;
  fontColor?: string;
}> = ({ html, lines, fontColor }) => {
  const [truncated, setTruncated] = useState(true);
  const [maxHeight, setMaxHeight] = useState(0);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const containerStyles = window.getComputedStyle(container);
      const lineHeight = parseFloat(containerStyles.lineHeight);
      const _maxHeight = lineHeight * lines;
      const contentHeight = container.scrollHeight;

      if (contentHeight > _maxHeight) {
        setShouldShowButton(true);
      }
      setMaxHeight(_maxHeight);
    }
  }, [html, lines]);

  const toggleTruncate = () => {
    setTruncated(!truncated);
  };

  const truncatedHTML = (
    <div
      ref={containerRef}
      className={cn(styles.Message, { [styles.ShowMore]: truncated })}
      style={
        truncated
          ? {
              // WebkitLineClamp: lines,
              // lineClamp: lines, // not working properly when html content has new lines or new paragraphs
              maxHeight: `${maxHeight}px`,
            }
          : {}
      }
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );

  return (
    <div className={styles.ExpandableRTERenderer}>
      <div className={styles.MessageContainer}>
        {truncatedHTML}
        {shouldShowButton && truncated && (
          <span className={styles.Ellipsis}>...</span>
        )}
      </div>
      {shouldShowButton && (
        <div className={styles.ShowMoreButton}>
          <p onClick={toggleTruncate}>{`View ${
            truncated ? 'more' : 'less'
          }`}</p>
          &nbsp;
          <SvgIcon
            display={'inline-flex'}
            type={`chevron${truncated ? 'Down' : 'Up'}`}
            color={toCssColor(fontColor, `#${colorBlack}`)}
            size="10px"
          />
        </div>
      )}
    </div>
  );
};

export const OwnerReviews: React.FC<IOwnerReviewsProps> = (
  props: IOwnerReviewsProps
) => {
  const [isMobile, setIsMobile] = useState(
    useMediaQuery(
      { maxDeviceWidth: tabletMax },
      undefined,

      (matches) => {
        setIsMobile(matches);
      }
    )
  );

  useRTETooltipPositionCorrector(isMobile);

  return (
    <div
      key={props.id}
      id={`cid-${props.id}`}
      className={cn('full-device-width', styles.OwnerReviews)}
      style={{
        ...(props.backgroundColor
          ? { backgroundColor: toCssColor(props.backgroundColor) }
          : {}),
        color: toCssColor(props.fontColor, `#${colorBlack}`),
      }}
    >
      <MediaQuery minWidth={0}>
        <OwnerReviewsContent isMobile={isMobile} {...props} />
      </MediaQuery>
    </div>
  );
};

interface OwnerReviewsContentProps extends IOwnerReviewsProps {
  isMobile: boolean;
}

const OwnerReviewsContent: React.FC<OwnerReviewsContentProps> = React.memo(
  ({
    isMobile,
    title,
    titleDisclaimer,
    reviewList,
    reviewCTA,
    tileBackgroundColor,
    tileFontColor,
    fontColor,
  }) => {
    const [expanded, setExpanded] = useState(false);
    const _reviews = reviewList && reviewList.length > 0 ? reviewList : [];
    const reviewsTotalIndex =
      _reviews.length === 0 ? -1 : Math.ceil(_reviews.length / 3) - 1;
    const reviewsIndeces = [...Array(reviewsTotalIndex + 1).keys()];
    const [expandedIndex, setExpandedIndex] = useState(
      reviewsTotalIndex > -1 ? 0 : -1
    ); // expanded starting index on mobile
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (ref.current) {
        const lastChild = ref.current.lastChild as HTMLElement;
        const parentElement = ref.current.parentElement as HTMLElement;
        parentElement.style.textAlign = lastChild?.style?.textAlign;
      }
    }, [ref.current]);

    const handleShowMore = () => {
      if (!isMobile) {
        setExpanded(!expanded);
      } else {
        setExpandedIndex(expandedIndex + 1);
      }
    };

    const showMore = () =>
      (!isMobile && !expanded && _reviews.length > 3) ||
      (isMobile && expandedIndex < reviewsTotalIndex);

    const renderReview = (review: IOwnerReview, idx: number) => {
      const _date = review.date ? new Date(review.date) : undefined;
      const _dateFormatted = _date
        ? _date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : undefined;
      return (
        <div key={idx} className={styles.ReviewContainer}>
          <div
            key={idx}
            className={cn(styles.Review, styles[`Review${idx % 3}`])}
            style={{
              ...(tileBackgroundColor
                ? { backgroundColor: toCssColor(tileBackgroundColor) }
                : {}),
              color: toCssColor(tileFontColor, `#${colorBlack}`),
            }}
          >
            {review.message && (
              <ExpandableRTERenderer
                html={review.message}
                lines={5}
                fontColor={tileFontColor}
              />
            )}
            <div
              className={styles.Author}
              dangerouslySetInnerHTML={{ __html: review.author as string }}
            />
            {_dateFormatted && (
              <p>
                <b>{_dateFormatted}</b>
              </p>
            )}
            {}
            <div>
              <Button
                className={styles.ReviewUrl}
                type="text"
                href={review.reviewUrl?.url}
                target={review.reviewUrl?.target || '_self'}
              >
                {review.reviewUrl?.name}
              </Button>
            </div>
          </div>
        </div>
      );
    };

    const getSliceEnd = (idx = expandedIndex) => {
      if (!isMobile) {
        return !expanded ? 3 : _reviews.length;
      }
      const initial = (idx + 1) * 3;
      return initial > _reviews.length ? _reviews.length : initial;
    };

    const showArrows = (list: IOwnerReview[]) => {
      if (!(list.length > 0)) return false;
      return list.length > 1;
    };

    const renderCTA = (cta: CallToAction) => {
      const _style: React.CSSProperties = {
        ...(cta.ctaColor ? { color: toCssColor(cta.ctaColor) } : {}),
        ...(cta.ctaColor ? { borderColor: toCssColor(cta.ctaColor) } : {}),
      };
      const buttonProps: ButtonProps = {
        type: toCamelCase(
          cta.ctaButtonType || 'primary'
        ) as keyof typeof ButtonTypeEnum,
        style: _style,
        buttonWidth: cta.ctaFullWidth,
        buttonSize: cta.ctaButtonSize || 'small',
        text:
          cta?.ctaUrl?.name || (!cta.ctaUrl?.url ? 'Show more' : 'Learn More'),
      };
      if (!cta.ctaUrl?.url || cta.ctaUrl?.url === '/') {
        buttonProps.onClick = handleShowMore;
      } else {
        buttonProps.href = cta.ctaUrl?.url;
        buttonProps.target = cta.ctaUrl?.target || '_self';
      }

      return (
        <div
          className={styles.CTA}
          style={{
            justifyContent: cta.ctaButtonAlignment || 'center',
          }}
        >
          <Button {...buttonProps} />
        </div>
      );
    };

    return (
      <>
        {reviewsTotalIndex > -1 && (
          <div
            id={`${isMobile ? 'mobile' : 'desktop'}-owner-reviews`}
            className={styles.Container}
          >
            <div
              className={cn(styles.Title, {
                [styles.TitleWithDisclaimer]: titleDisclaimer,
              })}
            >
              <div
                ref={ref}
                className={cn(styles.TitleText, {
                  [styles.TitleTextWithDisclaimer]: titleDisclaimer,
                })}
                dangerouslySetInnerHTML={{ __html: title as string }}
              />
              {titleDisclaimer && (
                <span
                  className={cn(`tt ${styles.HeaderDisclaimerTrigger}`)}
                  style={
                    fontColor && fontColor !== colorBlack
                      ? {
                          color: toCssColor(fontColor, `#${colorPrimary}`),
                        }
                      : {}
                  }
                >
                  *
                  <span
                    className="tt__content"
                    dangerouslySetInnerHTML={{
                      __html: titleDisclaimer as string,
                    }}
                  />
                </span>
              )}
            </div>

            <div
              className={cn(styles.Reviews, {
                [styles.ReviewsMobile]: isMobile,
              })}
            >
              {isMobile
                ? reviewsIndeces.slice(0, expandedIndex + 1).map((_, i) => {
                    const arr = _reviews.slice(i * 3, getSliceEnd(i));
                    return (
                      <Fragment key={i}>
                        <CustomCarousel
                          dotClassName={styles.CarouselDots}
                          className={styles.Carousel}
                          responsive={breakpoints}
                          showDots={isMobile && showArrows(arr)}
                          swipeable={isMobile}
                          removeArrowOnDeviceType={['mobile']}
                          arrows={!isMobile && showArrows(arr)}
                        >
                          {arr.map((r, idx) => renderReview(r, idx))}
                        </CustomCarousel>
                      </Fragment>
                    );
                  })
                : _reviews
                    .slice(0, getSliceEnd())
                    .map((r, idx) => renderReview(r, idx))}
            </div>

            {showMore() && reviewCTA && renderCTA(reviewCTA)}
          </div>
        )}
      </>
    );
  }
);
